import React, { useEffect, useMemo, useRef, useState } from 'react';

import './froala.scss';
import type {
  RichTextEvent,
  RichTextHighlight,
  RichTextRkpi,
  RichTextRkpiSpeed,
  RichTextStyle,
  RichTextSubjectForFroala,
} from './types';
import {
  checkElementExists,
  getListType,
  handleListFormat,
  isFroalaInComposedPath,
  unwrapElement,
} from './utils';
import { getHighlightSpanInParents, getHighlightsInSelection } from './utils/highlight';
import { getLinkValues, linksHandler } from './utils/links';
import { animateRkpi, getRkpiDataFromAttrs, getRkpiDurationFromSpeed } from './utils/rkpi';

export const RKPI_SPEED_OPTIONS: {
  [key in RichTextRkpiSpeed]: { duration: number; label: string };
} = {
  slow: {
    label: 'Slow',
    duration: 6000,
  },
  medium: {
    label: 'Medium',
    duration: 3000,
  },
  fast: {
    label: 'Fast',
    duration: 1500,
  },
};
export const RKPI_SPEEDS = Object.keys(RKPI_SPEED_OPTIONS);

interface Props {
  [key: string]: any;
  editorPlugins: {
    [key: string]: any;
    richTextSubject: RichTextSubjectForFroala;
    colorString: any;
  };
  id: string;
  path: {
    slideIndex: number;
    comboIndex: number;
    setIndex: number;
    groupIndex: number;
    elementIndex: number | null;
  } | null;
}

let LAST_FOCUS_ID = 'nope';

export const TEXT_STYLES: Record<
  RichTextStyle,
  {
    size: string;
    font: string;
    weight: string;
    color: string;
    controller: { label: string; fontSize: number; lineHeight: string };
  }
> = {
  'slide-title': {
    size: 'title-size',
    color: 'title-color',
    font: 'title-font',
    weight: 'title-style',
    controller: {
      label: 'Slide title',
      fontSize: 22,
      lineHeight: '27px',
    },
  },
  subtitle: {
    size: 'subtitle-size',
    color: 'subtitle-color',
    font: 'subtitle-font',
    weight: 'subtitle-style',
    controller: {
      label: 'Subtitle',
      fontSize: 18,
      lineHeight: '22px',
    },
  },
  normal: {
    size: 'paragraph-size',
    color: 'paragraph-color',
    font: 'paragraph-font',
    weight: 'paragraph-style',
    controller: {
      label: 'Normal text',
      fontSize: 13,
      lineHeight: '16px',
    },
  },
};

const comparePaths = (path: Props['path'], activePath: Props['path']): boolean => {
  if (!path || !activePath) return false;
  type K = keyof Props['path'];
  let isPathSame = true;
  for (const key in path) {
    if (path[key as K] !== activePath[key as K]) {
      isPathSame = false;
      break;
    }
  }
  return isPathSame;
};

export const Froala = ({ path, id, ...props }: Props) => {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const ResizeAxis = props.editorPlugins?.ResizeAxis;

  // const trackStory = editorPlugins.trackStory;
  const useAppDispatch = props.editorPlugins?.useAppDispatch;
  const useAppSelector = props.editorPlugins?.useAppSelector;
  const activePath = useAppSelector(
    (state: any) => state.editor.present.activePath,
  ) as Props['path'];
  const dispatch = useAppDispatch();
  const changeValue = props.editorPlugins?.changeValue;
  const lastRange = useRef<Range | null>(null);
  const selectedText = useRef<string | null>(null);

  const richTextSubject = props.editorPlugins?.richTextSubject;
  const rxjsFilter = props.editorPlugins?.rxjsFilter;

  const Froala = props.editorPlugins?.froalaEditor as any;
  const froalaInstance = useRef<any>(null);

  const highlightResetTimeout = useRef<NodeJS.Timeout | null>(null);

  const updateSelection = () => {
    const sel = document.getSelection();
    if (sel && sel.rangeCount > 0) {
      const r = sel.getRangeAt(0).cloneRange();
      selectedText.current = froalaInstance.current?.selection?.text();
      if (r) {
        lastRange.current = r;

        let el: HTMLElement | Node | null = r.commonAncestorContainer;
        if (!(el instanceof HTMLElement)) el = el.parentElement;

        if (el instanceof HTMLElement) {
          if (el.getAttribute('data-rkpi-active')) {
            const nRange = new Range();
            nRange.selectNode(el);
            sel.removeAllRanges();
            sel.addRange(nRange);
            lastRange.current = nRange;
          }
        }
      }
    }
  };

  useEffect(() => {
    if (!froalaInstance.current) return;
    if (comparePaths(path, activePath) && froalaInstance.current.events?.focus) {
      froalaInstance.current.events.focus();
    }
  }, [
    path?.slideIndex,
    path?.comboIndex,
    path?.setIndex,
    path?.groupIndex,
    path?.elementIndex,
    activePath?.slideIndex,
    activePath?.comboIndex,
    activePath?.setIndex,
    activePath?.groupIndex,
    activePath?.elementIndex,
  ]);

  useEffect(() => {
    if (froalaInstance.current || !Froala) return;

    const updateChanges = (html: string) => {
      const tmpTree = new DOMParser().parseFromString(html, 'text/html');

      const tmpHis = tmpTree.querySelectorAll('.fr-tmp-highlight');
      tmpHis.forEach((h) => {
        unwrapElement(h);
      });

      const his = tmpTree.querySelectorAll('[data-highlight-active]');
      const rkpis = tmpTree.querySelectorAll('[data-rkpi-active]');
      his.forEach((h) => {
        h.setAttribute('data-highlight-active', 'false');
      });
      rkpis.forEach((h) => {
        h.setAttribute('data-rkpi-active', 'false');
      });

      dispatch(
        changeValue({
          path: path,
          data: {
            name: props.isButton ? 'title' : 'content',
            content: tmpTree.body.innerHTML,
          },
          object: 'element',
        }),
      );
      // dispatch(trackStory({event: 'success update new text', screen: 'editorv4', params: {}}));
    };

    const refresh = (shouldSaveLastRange = true) => {
      const editor = froalaInstance.current;
      if (!editor) return;

      if (shouldSaveLastRange) updateSelection();

      const el = editor.selection?.element() as HTMLElement;
      if (!el) return;

      const computedStyle = window.getComputedStyle(el);
      const dsComputedStyle = window.getComputedStyle(document.documentElement);

      let { color, lineHeight, fontSize, textAlign, direction } = computedStyle;

      let textColor = color;
      const highlightSpan = getHighlightSpanInParents(el);
      if (highlightSpan) {
        const paragraph = editor.node.blockParent(el);
        if (paragraph) textColor = editor.$(paragraph).css('color');
      }

      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) return;
      const range = sel.getRangeAt(0);

      let selectedRkpi: RichTextRkpi | null = null;
      if (range.commonAncestorContainer instanceof HTMLElement && sel) {
        const rkpis = range.commonAncestorContainer.querySelectorAll('[data-rkpi-active]');
        for (let i = 0; i < rkpis.length; i++) {
          const rkpi = rkpis[i];
          if (sel.containsNode(rkpi, true)) {
            selectedRkpi = getRkpiDataFromAttrs(rkpi);
            break;
          }
        }
      }

      if (lineHeight === 'normal' || lineHeight == null || fontSize == null) lineHeight = '1.15';
      else {
        const parsedLineHeight = parseFloat(lineHeight.replace('px', ''));
        const parsedFontSize = parseFloat(fontSize.replace('px', ''));

        if (isNaN(parsedLineHeight) || isNaN(parsedFontSize)) lineHeight = '1.15';
        else lineHeight = `${parsedLineHeight / parsedFontSize}`;
      }

      let textStyle: RichTextStyle | null = null;
      if (checkElementExists(range.commonAncestorContainer, 'p')) textStyle = 'normal';
      else if (checkElementExists(range.commonAncestorContainer, 'h2')) textStyle = 'slide-title';
      else if (checkElementExists(range.commonAncestorContainer, 'h3')) textStyle = 'subtitle';
      else if (checkElementExists(range.commonAncestorContainer, 'li')) {
        for (const styKey in TEXT_STYLES) {
          const sty = TEXT_STYLES[styKey as keyof typeof TEXT_STYLES];
          if (
            computedStyle.getPropertyValue('font-size').trim() ===
            dsComputedStyle.getPropertyValue(`--${sty.size}`).trim()
          ) {
            textStyle = styKey as RichTextStyle;
            break;
          }
        }
      }

      richTextSubject.next({
        type: 'refresh',
        editorId: id,
        data: {
          selectedRkpi,
          fontSize: parseFloat(fontSize),
          textAlign,
          textColor: textColor,
          isBold: checkElementExists(range.commonAncestorContainer, 'strong'),
          isItalic: checkElementExists(range.commonAncestorContainer, 'em'),
          isUnderline: checkElementExists(range.commonAncestorContainer, 'u'),
          textStyle,
          isStrikethrough: checkElementExists(range.commonAncestorContainer, 's'),
          textDirection: direction as 'ltr' | 'rtl',
          highlight: highlightSpan
            ? {
                bgColor: highlightSpan.dataset.highlightBgColor ?? '',
                textColor: highlightSpan.dataset.highlightTextColor ?? '',
                animation: highlightSpan.dataset
                  .highlightAnimation as RichTextHighlight['animation'],
              }
            : null,
          listType: getListType(el),
          lineHeight,
          link: getLinkValues(el),
          subscript: checkElementExists(range.commonAncestorContainer, 'sub'),
          superscript: checkElementExists(range.commonAncestorContainer, 'sup'),
          selectionContent: sel?.toString(),
        },
      });
    };

    let isFocused = false;

    froalaInstance.current = new Froala(`#froala-editor-${id}`, {
      key: process.env.REACT_APP_FROALA_KEY,
      pastePlain: true,
      pasteDeniedAttrs: ['style', 'class', 'id', 'dir'],
      pasteDeniedTags: ['img', 'svg', 'script'],
      htmlRemoveTags: ['script'],
      pasteCleanupOption: 'clean',
      keepFormatOnDelete: true,
      toolbarContainer: '.toolbarContainer',
      placeholderText: 'Start typing here...',
      pluginsEnabled: [
        'align',
        'colors',
        'embedly',
        'entities',
        'fontFamily',
        'fontSize',
        'inlineStyle',
        'inlineClass',
        'lineBreaker',
        'lineHeight',
        'link',
        'lists',
        'paragraphFormat',
        'paragraphStyle',
        'quote',
        'save',
        'url',
      ],
      // iframe: true,
      // fontFamily: 'Inter',
      // iframeStyle: `* { font-family: 'Inter'; }`,
      // iframeStyleFiles: [
      // 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700'
      // 'https://fonts.googleapis.com/css?family=Roboto:400,300,300italic,400italic,700,700italic&subset=latin,vietnamese,latin-ext,cyrillic,cyrillic-ext,greek-ext,greek',
      // ],
      saveInterval: 1500,
      quickInsertEnabled: false,
      events: {
        'save.before': updateChanges,
        focus: () => {
          LAST_FOCUS_ID = id;
          isFocused = true;
          refresh();
        },
        blur: () => (isFocused = false),
        'buttons.refresh': function (this: any) {
          if (!isFocused) return false;

          refresh(false);
        },
        initialized: function (this: any) {
          this.opts.htmlAllowedTags.push('em-emoji');
          this.opts.htmlAllowedEmptyTags.push('em-emoji');
          this.opts.htmlAllowedAttrs.push('shortcodes');
          this.toolbar.hide();

          const editorEl = this.$el[0];
          if (editorEl) {
            editorEl.setAttribute('data-gramm', 'false');
            editorEl.setAttribute('data-gramm_editor', 'false');
            editorEl.setAttribute('data-enable-grammarly', 'false');
          }

          const btnEl = this.$box.closest('.buttonWrapper');
          if (btnEl.length > 0) {
            this.opts.placeholderText = '';
            this.placeholder.hide();
          }

          if (comparePaths(path, activePath)) {
            this.events.focus();
          }
        },

        mouseup: function () {
          updateSelection();
        },
        keyup: function () {
          updateSelection();
        },
        keydown: function (this: any, e: KeyboardEvent) {
          // START: Select for RKPI
          if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            const sel = document.getSelection();
            if (sel) {
              const r = sel.getRangeAt(0).cloneRange();

              if (r.startOffset !== r.endOffset) return;

              let sibling: HTMLElement | null = null;
              if (e.key === 'ArrowLeft' && r.startOffset === 0) {
                sibling = r.startContainer.previousSibling as HTMLElement;
              } else if (
                e.key === 'ArrowRight' &&
                r.startOffset === r.startContainer.textContent?.length
              ) {
                sibling = r.startContainer.nextSibling as HTMLElement;
              }

              if (
                !(sibling instanceof HTMLElement) ||
                !sibling.firstChild ||
                !sibling.closest('[data-rkpi-active]')
                // .getAttribute('data-rkpi-active')
              ) {
                return;
              }

              e.preventDefault();
              const nRange = new Range();
              nRange.selectNode(sibling.firstChild);
              sel.removeAllRanges();
              sel.addRange(nRange);
            }
          }
          // END: Select for RKPI

          if (e.key === 'Backspace' && this.selection) {
            const caretPos = this.selection.get();
            const focusNode = caretPos.focusNode;
            if (!focusNode || focusNode.nodeType !== Node.ELEMENT_NODE) return;
            const childNodes = focusNode.childNodes;
            const focusChild = childNodes[caretPos.focusOffset - 1];
            if (
              focusChild instanceof HTMLElement &&
              (focusChild.classList.contains('fast-versioning') ||
                focusChild.getAttribute('data-rkpi-active') ||
                focusChild.closest('em-emoji'))
            ) {
              const closest = focusChild.closest('em-emoji');
              if (closest) {
                closest.remove();
              } else {
                focusChild.remove();
              }
              this.save.save();
              refresh();
            }
          }
        },
      },
    });

    const handleToolbarChange = (e: RichTextEvent) => {
      const editor = froalaInstance.current;
      if (!editor) return;

      const el = editor.selection?.element();
      if (!el) return;

      switch (e.type) {
        case 'setBold':
          editor.commands.exec('bold');
          // editor.format.applyStyle('font-weight', e.data ? '600' : '400');
          refresh();
          break;
        case 'setItalic':
          editor.commands.exec('italic');
          // editor.format.applyStyle('font-style', e.data ? 'italic' : 'normal');
          refresh();
          break;
        case 'setUnderline':
          editor.commands.exec('underline');
          // toggleTextDecoration(editor, 'underline');
          refresh();
          break;
        case 'setStrikethrough':
          editor.commands.exec('strikeThrough');
          // toggleTextDecoration(editor, 'line-through');
          refresh();
          break;
        case 'setFontSize':
          const sel = window.getSelection();
          if (!sel || sel.rangeCount === 0) return;

          let shouldUseFroala = true;
          const hasHighlight = el.closest('[data-highlight-active]') as HTMLElement | null;
          const hasRkpi = el.closest('[data-rkpi-active]') as HTMLElement | null;
          if (hasHighlight || hasRkpi) {
            shouldUseFroala = false;
          }

          if (shouldUseFroala) {
            const blocks = editor.selection.blocks() as HTMLElement[];
            blocks.forEach((b) => {
              if (b instanceof HTMLElement) {
                const rkpis = b.querySelectorAll('[data-rkpi-active]');
                rkpis.forEach((rkpi) => {
                  if (sel.containsNode(rkpi, true)) {
                    (rkpi as HTMLElement).style.setProperty('font-size', e.data + 'px');
                  }
                });

                const his = b.querySelectorAll('[data-highlight-active]');
                his.forEach((hi) => {
                  if (sel.containsNode(hi, true)) {
                    (hi as HTMLElement).style.setProperty('font-size', e.data + 'px');
                  }
                });
              }
            });
            editor.format.applyStyle('font-size', e.data + 'px');
          } else {
            if (hasHighlight) {
              hasHighlight.style.setProperty('font-size', e.data + 'px');
              // const range = sel.getRangeAt(0);
              // const content = sel.toString();
              // range.deleteContents();
              // range.insertNode(
              //   document
              //     .createRange()
              //     .createContextualFragment(
              //       `<span style='font-size: ${e.data}px;'>${content}</span>`,
              //     ),
              // );
            } else if (hasRkpi) {
              hasRkpi.style.setProperty('font-size', e.data + 'px');
            }
          }

          editor.undo.saveStep();
          editor.save.save();
          refresh();
          break;
        case 'setTextAlign':
          editor.align.apply(e.data);
          refresh();
          break;
        case 'setTextColor': {
          const sel = window.getSelection();
          if (!sel || sel.rangeCount === 0) return;

          const hiSpan = getHighlightSpanInParents(el);
          if (el.getAttribute('data-rkpi-active')) {
            el.style.setProperty('color', e.data);
          } else if (hiSpan) {
            const wrapperSpan = document.createElement('span');
            wrapperSpan.style.setProperty('color', e.data);
            editor.$(hiSpan).wrap(`<span style="color: ${e.data};"></span>`);
          } else {
            const blockParent = editor.node.isBlock(el) ? el : editor.node.blockParent(el);
            if (blockParent instanceof HTMLElement) {
              const rkpis = blockParent.querySelectorAll('[data-rkpi-active]');

              rkpis.forEach((rkpi) => {
                if (rkpi instanceof HTMLElement) {
                  if (sel.containsNode(rkpi, true)) {
                    rkpi.style.setProperty('color', e.data);
                  }
                }
              });
            }

            editor.format.applyStyle('color', e.data);
          }

          editor.save.save();
          refresh();
          break;
        }
        case 'setTextStyle': {
          const sel = window.getSelection();
          if (!e.data || !sel || sel.rangeCount === 0) break;

          const vars = TEXT_STYLES[e.data];
          const blocks = editor.selection.blocks() as HTMLElement[];
          const newElTag = e.data === 'slide-title' ? 'h2' : e.data === 'subtitle' ? 'h3' : 'p';

          const savedRange = sel.getRangeAt(0).cloneRange();
          if (vars) {
            blocks.forEach((block) => {
              const fontSizeEls = block.querySelectorAll('[style*="font-size"]');
              fontSizeEls.forEach((e) => {
                if (e instanceof HTMLElement) {
                  e.style.removeProperty('font-size');
                }
              });

              if (block.tagName === 'LI') {
                block.style.setProperty('font-size', `var(--${vars.size})`);
                block.style.setProperty('color', `var(--${vars.color})`);
                block.style.setProperty('font-family', `var(--${vars.font})`);
                block.style.setProperty('font-weight', `var(--${vars.weight})`);
              } else if (block.tagName !== newElTag.toUpperCase()) {
                const t = document.createElement('span');
                t.className = 'fr-custom-caret-marker';
                savedRange.insertNode(t);

                block.style.removeProperty('font-size');
                block.style.removeProperty('line-height');
                const newEl = document.createElement(newElTag);
                for (let i = 0; i < block.attributes.length; i++) {
                  const attribute = block.attributes[i];
                  newEl.setAttribute(attribute.name, attribute.value);
                }
                while (block.firstChild) {
                  newEl.appendChild(block.firstChild);
                }
                block.replaceWith(newEl);
              }
            });
            refresh();
          }
          const p =
            savedRange.commonAncestorContainer instanceof HTMLElement
              ? savedRange.commonAncestorContainer
              : savedRange.commonAncestorContainer.parentElement;
          if (p instanceof HTMLElement) {
            const marker = p.querySelector('.fr-custom-caret-marker');
            if (marker) {
              const range = new Range();
              range.selectNode(marker);
              sel.removeAllRanges();
              sel.addRange(range);
              marker.remove();
            }
          }
          break;
        }
        case 'setTextDirection':
          editor.selection.save();
          editor.html.wrap(true, true, true, true);
          editor.selection.restore();

          const elements = editor.selection.blocks();

          editor.selection.save();

          for (let i = 0; i < elements.length; i++) {
            const element: HTMLElement = elements[i];
            if (element.nodeName === 'LI')
              element.parentElement?.setAttribute('data-direction', e.data);
            if (element !== editor.$el.get(0)) {
              editor
                .$(element)
                .css('direction', e.data)
                .css('text-align', e.data === 'rtl' ? 'right' : 'left')
                .removeClass('fr-temp-div');
            }
          }

          // Unwrap temp divs.
          editor.html.unwrap();

          // Restore selection.
          editor.selection.restore();

          refresh();
          break;
        case 'setHighlight': {
          const sel = window.getSelection();
          if (!sel) return;

          const endEl = editor.selection.endElement();
          const blocks = editor.selection.blocks() as HTMLElement[];
          const highlightsInSelection: HTMLElement[] = [];
          blocks.forEach((b) => {
            highlightsInSelection.push(...getHighlightsInSelection(b));
          });
          const inBetween = blocks.slice(1, blocks.length - 1);

          if (e.data == null) {
            const removeIfIsHighlight = (element: HTMLElement) => {
              const hiSpan = getHighlightSpanInParents(element);
              if (hiSpan) {
                editor.$(hiSpan).replaceWith(hiSpan.innerHTML);
              }
            };

            removeIfIsHighlight(el);
            removeIfIsHighlight(endEl);
            inBetween.forEach((element) => {
              const his = element.querySelectorAll('[data-highlight-active]');
              his.forEach((h) => {
                editor.$(h).replaceWith(h.innerHTML);
              });
            });
            editor.save.save();
            refresh();
            break;
          } else if (highlightsInSelection.length === 1) {
            const hiSpan = highlightsInSelection[0];
            if (hiSpan) {
              editor.$(hiSpan).attr({
                'data-highlight-active': 'false',
                'data-highlight-animation': e.data!.animation as string,
                'data-highlight-bg-color': e.data!.bgColor,
                'data-highlight-text-color': e.data!.textColor,
                style: `--var-highlight-bg-color: ${e.data!.bgColor};--var-highlight-text-color: ${
                  e.data!.textColor
                };`,
              });

              if (highlightResetTimeout.current != null) {
                clearTimeout(highlightResetTimeout.current);
                highlightResetTimeout.current = null;
              }

              highlightResetTimeout.current = setTimeout(() => {
                hiSpan.setAttribute('data-highlight-active', 'true');
                highlightResetTimeout.current = null;
              }, 400);

              editor.save.save();
              refresh();
              break;
            }
          }

          highlightsInSelection.forEach((n) => {
            editor.$(n).replaceWith(n.innerHTML);
          });

          const wrapElInTmpSpan = (child: Element | DocumentFragment) => {
            const spanTmp = document.createElement('span');
            spanTmp.className = 'fr-tmp-highlight';
            spanTmp.appendChild(child);
            return spanTmp;
          };

          if (editor.selection.isCollapsed()) break;
          const range = sel.getRangeAt(0);

          if (blocks.length > 1) {
            // Start
            const startRange = new Range();
            startRange.setStart(range.startContainer, range.startOffset);
            startRange.setEnd(range.startContainer, range.startContainer.textContent?.length!);
            const startPart = startRange.extractContents();
            startRange.insertNode(wrapElInTmpSpan(startPart));

            inBetween.forEach((n) => {
              const spanTmp = document.createElement('span');
              spanTmp.className = 'fr-tmp-highlight';
              while (n.firstChild) {
                spanTmp.appendChild(n.firstChild);
              }
              n.appendChild(spanTmp);
            });

            // End
            const endRange = new Range();
            endRange.setStart(range.endContainer, 0);
            endRange.setEnd(range.endContainer, range.endOffset);
            const endPart = endRange.extractContents();
            endRange.insertNode(wrapElInTmpSpan(endPart));
          } else {
            const selectedContent = range.extractContents();
            range.insertNode(wrapElInTmpSpan(selectedContent));
          }

          let hasSavedRange = false;
          blocks.forEach((block) => {
            const tmps = block.querySelectorAll('.fr-tmp-highlight');
            tmps.forEach((tmp) => {
              if (tmp instanceof HTMLElement) {
                if (!hasSavedRange && sel.rangeCount > 0) {
                  hasSavedRange = true;
                  const range = new Range();
                  range.selectNodeContents(tmp);
                  sel.removeAllRanges();
                  sel.addRange(range);
                }
                tmp.removeAttribute('class');
                tmp.setAttribute('data-highlight-active', 'true');
                tmp.setAttribute('data-highlight-animation', e.data!.animation as string);
                tmp.setAttribute('data-highlight-bg-color', e.data!.bgColor);
                tmp.setAttribute('data-highlight-text-color', e.data!.textColor);
                tmp.style.setProperty('--var-highlight-bg-color', e.data!.bgColor);
                tmp.style.setProperty('--var-highlight-text-color', e.data!.textColor);
              }
            });
          });

          editor.save.save();
          refresh();
          break;
        }
        case 'setDynamicVariable':
          editor.html.insert(
            `<span contenteditable="false" class="fast-versioning">${e.data}</span> `,
          );
          editor.save.save();
          refresh();
          break;
        case 'setList':
          handleListFormat(editor, e.data);
          refresh();
          break;
        case 'setLineHeight':
          editor.lineHeight.apply(e.data);
          refresh();
          break;
        case 'formatting':
          editor.commands.clearFormatting();
          refresh();
          break;
        case 'subscript':
          editor.commands.exec('subscript');
          refresh();
          break;
        case 'superscript':
          editor.commands.exec('superscript');
          refresh();
          break;
        case 'setLink':
          if (!e.data) return;
          linksHandler(editor, lastRange.current, selectedText.current, e.data);
          editor.save.save();
          refresh();
          break;
        case 'insertEmoji': {
          const sel = window.getSelection();
          const range = lastRange.current;
          if (!range || !sel) return;

          sel.removeAllRanges();
          sel.addRange(range);

          const emojiEl = document.createElement('em-emoji');
          emojiEl.contentEditable = 'false';
          emojiEl.setAttribute('shortcodes', e.data.emoji.shortcodes);
          range.insertNode(emojiEl);

          const newRange = new Range();
          newRange.setStartAfter(emojiEl);
          newRange.setEndAfter(emojiEl);
          sel.removeAllRanges();
          sel.addRange(newRange);

          editor.save.save();
          refresh();
          break;
        }
        case 'saveRkpi': {
          const sel = window.getSelection();
          if (!lastRange.current || !sel) return;
          const range = lastRange.current;
          const { to, speed, prefix, suffix } = e.data;

          sel.removeAllRanges();
          sel.addRange(range);

          const from = 0;
          const setAttrs = (span: HTMLElement) => {
            span.contentEditable = 'false';
            span.setAttribute('data-rkpi-active', 'true');
            span.setAttribute('data-rkpi-number-to', to.toString());
            span.setAttribute('data-rkpi-number-from', from.toString());
            span.setAttribute('data-rkpi-prefix', prefix);
            span.setAttribute('data-rkpi-suffix', suffix);
            span.setAttribute('data-rkpi-speed', speed);
            span.innerText = `${prefix}${from}${suffix}`;
          };

          if (range.commonAncestorContainer instanceof HTMLElement) {
            let shouldBreak = false;
            const allRkpis = range.commonAncestorContainer.querySelectorAll('[data-rkpi-active]');
            allRkpis.forEach((rkpi) => {
              if (sel.containsNode(rkpi, true)) {
                shouldBreak = true;
                setAttrs(rkpi as HTMLElement);
                animateRkpi(rkpi, from, to, getRkpiDurationFromSpeed(speed), prefix, suffix);
              }
            });
            if (shouldBreak) break;
          }

          const rkpiSpan = document.createElement('span');
          setAttrs(rkpiSpan);
          range.deleteContents();
          range.insertNode(rkpiSpan);
          // range.collapse(false);

          editor.save.save();
          refresh();

          animateRkpi(rkpiSpan, from, to, getRkpiDurationFromSpeed(speed), prefix, suffix);
          editor.$(`#froala-editor-${id} .fr-wrapper`)?.removeClass('show-placeholder');
          break;
        }
        case 'deleteRkpi': {
          if (!lastRange.current) return;
          const range = lastRange.current;

          const selection = window.getSelection();
          if (!selection || !range) return;

          if (!(range.commonAncestorContainer instanceof HTMLElement)) return;

          selection.removeAllRanges();
          selection.addRange(range);

          const rkpis = range.commonAncestorContainer.querySelectorAll('[data-rkpi-active]');
          rkpis.forEach((rkpi) => {
            if (selection.containsNode(rkpi, true)) {
              rkpi.remove();
            }
          });

          editor.save.save();
          refresh();
          break;
        }
      }
    };

    const handleDocMouseDown = (e: MouseEvent) => {
      const editor = froalaInstance.current;
      const editorRef = elementRef.current;
      if (!editor || !editorRef) return;

      const activeElement = (e.target as Element).closest('.element.activeMode.RichText');
      const activeSettings = (e.target as Element).closest('.fr-toolbar');
      if (!activeElement && !activeSettings) {
        editor.$el?.blur();
        editor.selection?.clear();
      }

      if (LAST_FOCUS_ID === id && editor.events?.focus) {
        const composedPath = e.composedPath();
        const foundFroala = isFroalaInComposedPath(composedPath);
        if (!editorRef.contains(e.target as Node)) {
          const target = e.target as HTMLElement;

          let shouldSkip = false;
          if (
            ['INPUT', 'TEXTAREA', 'EM-EMOJI-PICKER'].includes(target.nodeName) ||
            composedPath.find((c) => c instanceof HTMLElement && c.nodeName === 'BUTTON') ||
            composedPath.find(
              (c) =>
                c instanceof HTMLElement &&
                c.getAttribute('data-rich-text-focus-ignore') === 'true',
            )
          ) {
            shouldSkip = true;
          }

          if (!shouldSkip && !foundFroala) {
            e.preventDefault();
            if (activeElement) editor.events.focus();
          }
        }
      }
    };

    const sub = richTextSubject
      .pipe(rxjsFilter((e: RichTextEvent) => e.editorId === id))
      .subscribe(handleToolbarChange);

    document.addEventListener('mousedown', handleDocMouseDown);
    return () => {
      if (froalaInstance.current && froalaInstance.current.destroy) {
        froalaInstance.current.destroy();
        const lineBreaker = document.querySelector('.fr-line-breaker');
        if (lineBreaker) {
          lineBreaker.remove();
        }

        richTextSubject.next({ type: 'onFroalaDestroy', editorId: id, data: true });
      }
      if (highlightResetTimeout.current != null) {
        clearTimeout(highlightResetTimeout.current);
      }
      document.removeEventListener('mousedown', handleDocMouseDown);
      sub.unsubscribe();
    };
  }, []);

  // ======= resize ========
  const [width, setWidth] = useState<number>(props.width);
  const [elementDomWidth, setElementDomWidth] = useState<number>(0);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  useEffect(() => {
    setWidth(props.width);
  }, [props.width]);
  const emitWidth = (width: number): void => setWidth(width);

  useEffect(() => {
    if (elementRef && elementRef.current) setContainerWidth(elementRef.current.clientWidth);
    if (elementRef && elementRef.current) {
      const richText: HTMLDivElement | null = elementRef.current.closest('.RichText');
      if (richText) setElementDomWidth(richText.clientWidth);
    }
  }, [width]);
  // ======= resize end ========

  const finalContent = props.content && props.content.length ? props.content : '';

  const froalaEditorContainer = useMemo(
    () => (
      <div
        id={`froala-editor-${id}`}
        ref={elementRef}
        className="froala-editor"
        style={{
          width: '100%',
          minHeight: 10,
        }}
        dangerouslySetInnerHTML={{ __html: finalContent }}
      />
    ),
    [],
  );

  return (
    <div
      data-path={
        path?.slideIndex +
        '-' +
        path?.comboIndex +
        '-' +
        path?.setIndex +
        '-' +
        path?.groupIndex +
        '-' +
        path?.elementIndex
      }
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: props.horizontalAlignment || 'inherit',
        width: '100%',
        position: 'relative',
      }}>
      {ResizeAxis && (
        <ResizeAxis
          widthProps={{
            width: String(width || 100),
            property: 'width',
            min: 10,
            max: 100,
            elementDomWidth,
            containerWidth,
            emitWidth,
          }}
          heightProps={{ height: 0, property: '', containerHeight: 0, emitHeight: () => {} }}
          containerHeight={elementRef.current?.offsetHeight}
        />
      )}
      <div style={{ width: (width || 100) + '%' }}>{froalaEditorContainer}</div>
    </div>
  );
};
